import Link from "next/link";
import { useSession } from "next-auth/react";
import { AvatarTag, SvgIcon, SvgIconsEnums } from "@ilg-credability/credability-web-components";
import Image from "next/image";
import styles from "./MenuOverlayNav.module.css";
import { MainCategoryEnum, PageEnum } from "@/types/Analytics";
import { Analytics } from "@/utilities/analytics";
import { onLogout, useUser } from "@/services/auth";
import { useEffect } from "react";

export const MenuOverlayNav = ({
  isActive,
  onClose,
}: {
  isActive: boolean;
  onClose: (targetScreen?: PageEnum) => void;
}) => {
  const { data: session } = useSession();

  const { trigger: getUser, userDetails, userDetailsIsLoading, userDetailsIsError } = useUser();

  useEffect(() => {
    if (isActive) {
      getUser()
    }
  }, [getUser, isActive])

  return (
    <div
      className={`${styles.menuOverlay} ${isActive ? styles.active : ''}`}
      id="menu-overlay"
      data-testid="menu-overlay"
    >
      <div
        className={`${styles.backDrop} ${isActive ? styles.active : ''} tablet-down-hide`}
        onClick={() => onClose}
      />
      <div className={`${styles.menuContainer} ${isActive ? styles.menuContainerActive : ''}`}>
        <div className={styles.navigationWrapper}>
          <button
            onClick={() => onClose()}
            className={`${styles.menuCloseIcon} tablet-down-hide ${styles.clickable}`}
          >
            <SvgIcon
              icon={SvgIconsEnums.CLOSE}
              size={{ height: 24 }}
              fill={'var(--cred-color-light-grey)'}
              loading={false}
              className={""}
            />
          </button>
          <div className={styles.avatarContainer}>
            {!userDetailsIsLoading && !userDetailsIsError && (
              <AvatarTag
                text={`${userDetails?.firstName ?? ""} ${
                  userDetails?.lastname ?? ""
                }`}
                subtext={userDetails?.emailAddress ?? ""}
                imgSrc={""}
              />
            )}
          </div>
          <Link
            href="/settings/marketing-preferences"
            onClick={() => onClose(PageEnum.MARKETING_PREFERENCES)}
            className={`${styles.menuItemLink} ${styles.clickable}`}
          >
            <div className={styles.menuItem}>
              <SvgIcon
                icon={SvgIconsEnums.ANNOUNCEMENT}
                size={{ height: 24 }}
                fill={'var(--cred-color-light-primary)'}
                loading={false}
                className={""}
              />
              Marketing preferences
            </div>

            <div className="tablet-up-hide">
              <SvgIcon
                icon={SvgIconsEnums.RIGHT_ARROW}
                size={{ height: 24 }}
                fill={'var(--cred-color-light-grey)'}
                loading={false}
              />{" "}
            </div>
          </Link>
          <Link
            href="/settings/delete-account"
            onClick={() => onClose(PageEnum.DELETE_ACCOUNT)}
            className={`${styles.menuItemLink} ${styles.clickable}`}
          >
            <div className={styles.menuItem}>
              <SvgIcon
                icon={SvgIconsEnums.BROKEN_HEART}
                size={{ height: 24 }}
                fill={'var(--cred-color-light-primary)'}
                loading={false}
              />{" "}
              Delete account
            </div>

            <div className="tablet-up-hide">
              <SvgIcon
                icon={SvgIconsEnums.RIGHT_ARROW}
                size={{ height: 24 }}
                fill={'var(--cred-color-light-grey)'}
                loading={false}
              />{" "}
            </div>
          </Link>
          <Link
            href="/settings/legal"
            onClick={() => onClose(PageEnum.LEGAL)}
            className={`${styles.menuItemLink} ${styles.clickable}`}
          >
            <div className={styles.menuItem}>
              <SvgIcon
                icon={SvgIconsEnums.LEGAL}
                size={{ height: 24 }}
                fill={'var(--cred-color-light-primary)'}
                loading={false}
              />{" "}
              Legal
            </div>

            <div className="tablet-up-hide">
              <SvgIcon
                icon={SvgIconsEnums.RIGHT_ARROW}
                size={{ height: 24 }}
                fill={'var(--cred-color-light-grey)'}
                loading={false}
              />{" "}
            </div>
          </Link>

          <Link
            href={""}
            className={`${styles.logoutLink} ${styles.clickable}`}
            onClick={() => {
              Analytics.logEvent("cta_log_out", {
                action: "log_out",
                main_category: MainCategoryEnum.MENU,
                referral_screen: "menu",
              });
              onLogout(session?.idToken);
            }}
          >
            <SvgIcon
              icon={SvgIconsEnums.LOGOUT}
              size={{ height: 24 }}
              fill={'var(--cred-color-light-primary)'}
              loading={false}
            />{" "}
            Logout
          </Link>
        </div>

        <div className={styles.menuCopyright}>
          <Link href={"/"} className={styles.logoAnchor}>
            <Image
              src={"/logo.svg"}
              alt="CredAbility Logo"
              width={108}
              height={29}
              priority={true}
            />
          </Link>
          <p className={styles.dark}> Made with 💜 in Manchester</p>
          <p>
            Copyright {new Date().getFullYear()} CredAbility
          </p>
        </div>
      </div>
    </div>
  );
};
